import type { Booking } from '@core/interfaces/booking.interface';
import type { BookingMirWorkflow } from '@services/booking-mir-workflow.service';
import { isOtaBooking } from '@utils/booking-channel.util';

import { BookingGeneralState, BookingMirState } from './booking-states.config';
import {
  type BookingAction,
  BookingStatus,
  bookingStatusView,
  isCancelledBookingStatus,
  normalizeBookingStatus,
} from './booking-status.config';

export interface BookingGuestAccess {
  /** The booking's guest data travels the channel "booking legal" flow (Idempotency-Key + legal version). */
  readonly legal: boolean;
  readonly edit: boolean;
  readonly send: boolean;
  /** Hospedajes already holds this data: a new send is only meaningful after the guests changed. */
  readonly resendRequiresChange: boolean;
}

const LOCKED: BookingGuestAccess = {
  legal: false,
  edit: false,
  send: false,
  resendRequiresChange: false,
};

const LOCAL_ALREADY_SENT = new Set(['BOOKING_NOTIFICATION_PENDING', 'CHECKIN_PENDING']);
const CANONICAL_ACTIVE_GENERAL = new Set<string>([
  BookingGeneralState.CHECKIN_SCHEDULED,
  BookingGeneralState.CHECKOUT_PENDING,
  BookingGeneralState.CHECKOUT_HOST_CONFIRMATION_PENDING,
  BookingGeneralState.BOOKING_CHECKOUT_ERROR,
]);
const CANONICAL_EDITABLE_MIR = new Set<string>([
  BookingMirState.MIR_START,
  BookingMirState.GUEST_MIR_DATA_PENDING,
  BookingMirState.MIR_DATA_READY_PAYMENT_PENDING,
  BookingMirState.SEND_GUEST_MIR_DATA_PENDING,
  BookingMirState.BOOKING_NOTIFICATION_PENDING,
  BookingMirState.BOOKING_NOTIFICATION_ERROR,
  BookingMirState.CHECKIN_PENDING,
  BookingMirState.CHECKIN_NOTIFICATION_PENDING,
  BookingMirState.CHECKIN_ERROR,
  BookingMirState.MIR_COMPLETED,
]);
const CANONICAL_SEND_BLOCKED_MIR = new Set<string>([
  BookingMirState.MIR_DATA_READY_PAYMENT_PENDING,
]);

/**
 * True when the backend exposes a legal read model for the booking, which it does only for
 * bookings owned by a sales channel. Their guest writes are governed by `capabilities`, not by the
 * status matrix, and every write must quote the legal version it was read at.
 */
export function isLegalManagedBooking(booking: Booking | null | undefined): boolean {
  return !!booking && booking.legalStatus != null && booking.legalStatus !== '';
}

/**
 * What the host may do with a booking's guests. A local booking follows the shared status matrix
 * (MANAGE_GUESTS = backend guestUpdateAllowed, SEND_GUESTS = mirDataSendAllowed); a channel
 * booking follows the capabilities the backend computed from its legal status, which fail closed.
 */
export function bookingGuestAccess(booking: Booking | null | undefined): BookingGuestAccess {
  if (!booking) {
    return LOCKED;
  }
  if (isLegalManagedBooking(booking)) {
    const capabilities = booking.capabilities;
    return {
      legal: true,
      edit: capabilities?.legalGuestUpdateAllowed === true,
      send: capabilities?.legalSendAllowed === true || capabilities?.legalRetryAllowed === true,
      resendRequiresChange: booking.legalStatus === 'CHECKIN_PENDING',
    };
  }
  if (isOtaBooking(booking)) {
    return LOCKED;
  }
  if (booking.mirState && CANONICAL_ACTIVE_GENERAL.has(booking.bookingStatus)) {
    const edit = CANONICAL_EDITABLE_MIR.has(booking.mirState);
    return {
      legal: false,
      edit,
      send: edit && !CANONICAL_SEND_BLOCKED_MIR.has(booking.mirState),
      resendRequiresChange: MIR_ALREADY_SENT.has(booking.mirState),
    };
  }
  if (normalizeBookingStatus(booking.bookingStatus) === BookingStatus.CHECKIN_SCHEDULED) {
    const edit = booking.manualMirManaged !== true;
    return { legal: false, edit, send: edit, resendRequiresChange: false };
  }
  const view = bookingStatusView(booking.bookingStatus);

  return {
    legal: false,
    edit: view.actions.includes('MANAGE_GUESTS'),
    send: view.actions.includes('SEND_GUESTS'),
    resendRequiresChange: view.status !== null && LOCAL_ALREADY_SENT.has(view.status),
  };
}

/** Canonical MIR states (`BookingWorkflow.Mir`) after Hospedajes acknowledged the data. */
export const MIR_ACCEPTED_STATES: ReadonlySet<string> = new Set([
  BookingMirState.CHECKIN_PENDING,
  BookingMirState.CHECKIN_NOTIFICATION_PENDING,
  BookingMirState.MIR_COMPLETED,
]);

/** Canonical MIR states once the guests left for Hospedajes, answered or not. */
export const MIR_SENT_STATES: ReadonlySet<string> = new Set([
  BookingMirState.BOOKING_NOTIFICATION_PENDING,
  BookingMirState.CHECKIN_ERROR,
  ...MIR_ACCEPTED_STATES,
]);

const MIR_ALREADY_SENT = new Set<string>([
  BookingMirState.BOOKING_NOTIFICATION_PENDING,
  BookingMirState.CHECKIN_PENDING,
]);

/**
 * What a local booking's workflow lets the host do with its guests (GET /bookings/{id}/workflow).
 * `canEditGuests` is the backend's own guard on PUT /guests and POST /checkin (MIR active: not
 * skipped, cancelled or closing). The send is held back only while a pending payment blocks it
 * (MIR_DATA_READY_PAYMENT_PENDING); the data check itself runs on the screen and again on submit.
 * Null when the view does not carry the capability, so the caller falls back to the status matrix.
 */
export function workflowGuestAccess(
  workflow: BookingMirWorkflow | null | undefined,
  mirState: string | null | undefined
): BookingGuestAccess | null {
  if (typeof workflow?.canEditGuests !== 'boolean') {
    return null;
  }
  const edit = workflow.canEditGuests;
  return {
    legal: false,
    edit,
    send: edit && mirState !== BookingMirState.MIR_DATA_READY_PAYMENT_PENDING,
    resendRequiresChange: MIR_ALREADY_SENT.has(mirState ?? ''),
  };
}

/**
 * When the guests last left for Hospedajes, as far as the backend tells: the check-in-day send it
 * stamps in `lastMirDataSentDate`, or the state history's last send. A booking send enters
 * BOOKING_NOTIFICATION_PENDING; a corrected check-in send moves CHECKIN_ERROR straight to
 * CHECKIN_PENDING. Null when the booking has never been sent.
 */
export function lastSendToHospedajes(booking: Booking | null | undefined): number | null {
  if (!booking) {
    return null;
  }
  const history = booking.stateHistory ?? [];
  const candidates = [parseDate(booking.lastMirDataSentDate)];
  history.forEach((entry, index) => {
    const state = normalizeBookingStatus(entry.state);
    const previous = index > 0 ? normalizeBookingStatus(history[index - 1].state) : null;
    if (
      state === BookingStatus.BOOKING_NOTIFICATION_PENDING ||
      (state === BookingStatus.CHECKIN_PENDING && previous === BookingStatus.CHECKIN_ERROR)
    ) {
      candidates.push(parseDate(entry.changedAt));
    }
  });
  const known = candidates.filter((value): value is number => value !== null);

  return known.length ? Math.max(...known) : null;
}

/**
 * Whether the guests were saved after the last send to Hospedajes. A booking never sent counts as
 * changed; one never re-saved since the send does not.
 */
export function guestsChangedSinceLastSend(booking: Booking | null | undefined): boolean {
  const sentAt = lastSendToHospedajes(booking);
  if (sentAt === null) {
    return true;
  }
  const updatedAt = parseDate(booking?.lastGuestsUpdateDate);

  return updatedAt !== null && updatedAt > sentAt;
}

function parseDate(value: string | null | undefined): number | null {
  if (!value) {
    return null;
  }
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * The actions a booking offers on its settings grid: the status matrix filtered by guest access,
 * plus actions granted by the canonical local workflow or a channel's legal capabilities. A local
 * booking's guest access comes from its workflow view when loaded, since the booking GET carries
 * no MIR state; the guests page stays reachable while MIR is skipped so it can be reactivated.
 */
export function bookingActionsFor(
  booking: Booking | null | undefined,
  workflow?: BookingMirWorkflow | null
): BookingAction[] {
  const local = !!booking && !isLegalManagedBooking(booking) && !isOtaBooking(booking);
  const view = bookingStatusView(booking?.bookingStatus);
  const statusAccess = bookingGuestAccess(booking);
  const access = (local ? workflowGuestAccess(workflow, workflow?.mirState) : null) ?? statusAccess;
  const reachable =
    access.edit ||
    (statusAccess.edit && view.actions.includes('MANAGE_GUESTS')) ||
    (local &&
      workflow?.manualMirManaged === true &&
      !isCancelledBookingStatus(booking?.bookingStatus));
  const actions = view.actions.filter(
    action =>
      (action !== 'MANAGE_GUESTS' || reachable) &&
      (action !== 'SEND_GUESTS' || access.send) &&
      (action !== 'CANCEL' || isOtaBooking(booking) || workflow?.canCancelLocally === true)
  );
  if (
    booking &&
    !isOtaBooking(booking) &&
    workflow?.canCancelLocally === true &&
    !actions.includes('CANCEL')
  ) {
    actions.push('CANCEL');
  }
  if (reachable && !actions.includes('MANAGE_GUESTS')) {
    actions.push('MANAGE_GUESTS');
  }
  if (access.send && !actions.includes('SEND_GUESTS')) {
    actions.push('SEND_GUESTS');
  }

  return actions;
}
